import React, { FC, useContext, useEffect, useState } from "react";
import { Box, Button, ListItemText, Tooltip, Typography, Menu, MenuItem } from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import theme from "common/theme/theme";
import useProjectStore, { INetwork } from "common/store/ProjectStore";
import useSettingsStore from "common/store/SettingsStore";
import { LanguageContext } from "common/contexts/LanguageContext";
import BladeIconButton from "./BladeIconButton";
import { getNetworkAntennaName } from "common/utils/networkExtensions";
import useDeviceStore from "common/store/DeviceStore";
import { SharedProjectContext } from "components/project/Project";
import SimpleModal from "common/components/dialogs/SimpleModal";
import DeviceCategory from "common/constants/DeviceCategory";

interface IProps {
  network: INetwork;
}

const NetworkListItem: FC<IProps> = (props) => {
  const { network } = props;

  const {
    activeNetwork,
    setActiveNetwork,
    recalculateNetworkUsage,
    setNetworkVisiblity,
    deleteNetwork,
    networkContainers,
    addDeviceInstance,
  } = useProjectStore();
  const { settings } = useSettingsStore();
  const { getString } = useContext(LanguageContext);
  const { lookupCategory } = useDeviceStore();
  const { setIsBusyBackgroundActive, setNetworkDeleted } = useContext(SharedProjectContext);

  const [anchorActionsEl, setAnchorActionsEl] = useState<HTMLButtonElement | null>(null);
  const [actionsOpen, setActionsOpen] = useState<boolean>(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);

  useEffect(() => {
    setIsBusyBackgroundActive(isConfirmOpen);
  }, [isConfirmOpen, setIsBusyBackgroundActive]);

  useEffect(() => {
    (async () => {
      if (network.metaData.isUsagePercentDirty) {
        await recalculateNetworkUsage(settings.apiBaseUrl, network.networkID);
      }
    })();
  }, [network.metaData, network.networkID, settings.apiBaseUrl, recalculateNetworkUsage]);

  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorActionsEl(event.currentTarget);
    setActionsOpen(true);
  };

  const handleDeleteMenuClick = () => {
    setActionsOpen(false);
    setIsConfirmOpen(true);
  };

  const handleDeleteConfirmation = async () => {
    setIsConfirmOpen(false);
    deleteNetwork(network.networkID);
    setNetworkDeleted(true);
  };

  const handleMoveAndDelete = async () => {
    var networks = networkContainers.find((con) => con.networkContainerID === network.networkContainerID)!.networks
    var otherNetworkID = networks.find(net => net.networkID !== network.networkID)?.networkID;
    network.deviceInstances.forEach((dev) => {
      if(lookupCategory(dev.deviceID) !== DeviceCategory.Antenna) {
        var newDev = { ...dev };
        newDev.rowVersion = null;
        newDev.updatedDate = null;
        addDeviceInstance(newDev, otherNetworkID!);
      }
    });
    handleDeleteConfirmation();
  };

  const handleOnClickSelectActiveNetwork = (networkID: number) => {
    setActiveNetwork(networkID);
  };

  const handleOnClickToggleVisibility = () => {
    setNetworkVisiblity(network.networkID, !network.metaData.isVisible);
  };

  return (
    <>
      <ListItemText
        sx={{
          backgroundColor:
            network.networkID === activeNetwork?.networkID ? theme.palette.grey[50] : theme.palette.common.white,
          color: theme.palette.common.black,
          p: "8px",
        }}
        primaryTypographyProps={{ fontSize: "12px" }}
      >
        <Box display="flex" justifyContent="space-between">
          <Box display="flex">
            {network.networkID === activeNetwork?.networkID ? (
              <CheckOutlinedIcon sx={{ width: "16px", height: "16px" }} />
            ) : (
              <BladeIconButton disableRipple sx={{ p: 0 }} onClick={handleOnClickToggleVisibility}>
                {network.metaData.isVisible ? (
                  <Tooltip title={getString("tt_hide_network")}>
                    <VisibilityOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                  </Tooltip>
                ) : (
                  <Tooltip title={getString("tt_show_network")}>
                    <VisibilityOffOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                  </Tooltip>
                )}
              </BladeIconButton>
            )}
            <Typography variant="innerLevelBlade" sx={{ width: "36px", padding: "2px", pl: "4px" }}>
              {network.metaData.usagePercent}%
            </Typography>
            <Button
              variant="text"
              disableRipple
              sx={{
                padding: 0,
                color: theme.palette.common.black,
                "&:hover": {
                  color: theme.palette.primary.light,
                },
              }}
              onClick={() => handleOnClickSelectActiveNetwork(network.networkID)}
            >
              <Typography variant="innerLevelBlade" sx={{ padding: "2px" }}>
                {getNetworkAntennaName(network, lookupCategory, getString("mnu_unnamed_nwk"))}
              </Typography>
            </Button>
          </Box>
          <BladeIconButton
            disableRipple
            onClick={handleActionsClick}
            sx={{ p: 0, ml: "8px", color: theme.palette.common.black, align: "right" }}
          >
            <MoreHorizIcon sx={{ width: "16px", height: "16px" }} />
          </BladeIconButton>
          <Menu anchorEl={anchorActionsEl} open={actionsOpen} onClose={() => setActionsOpen(false)}>
            <MenuItem
              onClick={() => handleDeleteMenuClick()}
              disabled={
                networkContainers.find((con) => con.networkContainerID === network.networkContainerID)!.networks
                  .length === 1
              }
            >
              {getString("cmn_delete")}
            </MenuItem>
          </Menu>
        </Box>
      </ListItemText>
      <SimpleModal
        title={getString("dlg_del_nwk_title")}
        maxWidth="md"
        isOpen={isConfirmOpen}
        setOpen={setIsConfirmOpen}
      >
        <Box sx={{paddingTop: "24px"}}>
        <Button variant="contained" color="primary" onClick={handleDeleteConfirmation}>
          {getString("tt_delete_antenna_all")}
        </Button>
        <Button variant="contained" color="primary" onClick={handleMoveAndDelete}>
          {getString("tt_delete_antenna_ move")} {getNetworkAntennaName(network, lookupCategory, getString("mnu_unnamed_nwk"))}
        </Button>
        <Button variant="outlined" color="primary" onClick={() => setIsConfirmOpen(false)}>
          {getString("cmn_cancel")}
        </Button>
        </Box>
      </SimpleModal>
    </>
  );
};

export default NetworkListItem;
